import { find, isArray, isPlainObject, sumBy } from 'lodash';
import parse from 'html-react-parser';
import currency from 'currency.js';
import { format as dateFormat } from 'date-fns';
import { FORMAT_QT_DATE } from 'utils/helpers/datetime';

// Converts deeply nested params hash to an array of keys and values suitable for appending onto a URL
const convertToSearchParam = (obj, prefixKey = '') => {
  let keyValuePairs = [];
  Object.keys(obj).forEach((hashKey) => {
    const key = prefixKey.length > 0 ? `${prefixKey}[${hashKey}]` : hashKey;
    const value = obj[hashKey];
    if (isPlainObject(value)) {
      keyValuePairs = [...keyValuePairs, ...convertToSearchParam(value, key)];
    } else if (isArray(value)) {
      value.forEach((item) => {
        if (isPlainObject(item)) {
          keyValuePairs = [
            ...keyValuePairs,
            ...convertToSearchParam(item, `${key}[]`),
          ];
        } else {
          keyValuePairs.push([`${key}[]`, item]);
        }
      });
    } else {
      keyValuePairs.push([key, value]);
    }
  });

  return keyValuePairs;
};

export function getParamFromUrl(keyword) {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  return urlParams.get(keyword);
}

export function generateURL(path, params) {
  const apiPrefix = ecom.craft3config.api_prefix || '';
  const API_BASE_URL = `${window.location.origin}${apiPrefix}/ecom/api/v1`;
  let finalURL = API_BASE_URL + path;

  if (params) {
    const searchParams = new URLSearchParams();

    const paramsArray = convertToSearchParam(params);
    paramsArray.forEach((param) => {
      searchParams.append(...param);
    });

    finalURL = `${finalURL}?${searchParams}`;
  }

  return finalURL;
}

export function price(
  value,
  configs = { fromCents: true, omitZeroCents: false }
) {
  return value === undefined
    ? ''
    : currency(value, configs).format(
        // https://github.com/scurker/currency.js/issues/191
        (instance, options) => {
          // Call original format function to get its string value
          const format = options.format(instance, options);

          if (instance.cents() === 0 && configs.omitZeroCents) {
            // Trim decimal and any trailing zeros
            return format.slice(0, format.length - (options.precision + 1));
          }

          return format;
        }
      );
}

// Retured data looks like this: `{ Adult: { price: 16500, count: 1 }, Child: { price: 8900, count: 1 } }`.
export function processPassengerType(
  passengerSplits,
  passengers,
  passengerTypes
) {
  const processedPassengerType = {};

  passengerSplits.forEach((item) => {
    const passenger = find(passengers, { id: item.consumer_id });
    const passengerType = find(passengerTypes, {
      id: passenger.passenger_type_id,
    });
    const { name } = passengerType;

    processedPassengerType[name] = processedPassengerType[name] || {
      price: item.gross_in_cents,
      consumer_id: item.consumer_id,
    };
    const existingCount = processedPassengerType[name].count;
    const count = existingCount + 1 || 1;

    processedPassengerType[name].count = count;
  });

  return processedPassengerType;
}

// Calculate original price from a reservations attributes list
export function getOriginalPrice(reservations) {
  if (!reservations) {
    return 0;
  }

  return reservations
    .map(
      (reservation) =>
        reservation.pre_adjusted_gross_including_packaged_item_in_cents
    )
    .reduce((a, b) => a + b, 0);
}

export function getTotalGrossInCents(reservations) {
  if (!reservations) {
    return 0;
  }

  return reservations
    .map((reservation) => reservation.gross_in_cents)
    .reduce((a, b) => a + b, 0);
}

export function sumOfGrossInCentsMinusCommission(reservations) {
  if (!reservations) {
    return 0;
  }

  return (
    sumBy(reservations, 'gross_in_cents') -
    sumBy(reservations, 'pre_adjusted_commission_in_cents')
  );
}

export function getTotalGrossIncludePackagedItem(reservations) {
  if (!reservations) {
    return 0;
  }

  return sumBy(reservations, 'gross_including_packaged_item_in_cents');
}

export function getDiscount(booking) {
  let discount = 0;

  if (booking.adjustments_attributes) {
    booking.adjustments_attributes.forEach((adjustment) => {
      discount += adjustment.gross_in_cents;
    });
  }

  if (booking.reservations_attributes) {
    booking.reservations_attributes.forEach((reservation) => {
      discount +=
        reservation.pre_adjusted_gross_including_packaged_item_in_cents -
        reservation.gross_including_packaged_item_in_cents;
    });
  }

  return Math.abs(discount);
}

export const CheckoutDialogStatesEnum = {
  closed: 0,
  paymentPending: 1,
  error: 2,
};
Object.freeze(CheckoutDialogStatesEnum);

export const phoneNumber =
  window.ecom?.craft3config?.phoneNumber?.plainText ||
  window.ecom?.craft3config?.phoneNumber?.display;
export const phoneNumberLink =
  window.ecom?.craft3config?.phoneNumber?.plainText2 ||
  window.ecom?.craft3config?.phoneNumber?.tel;

export const parsePhoneNumberLink = (message) =>
  parse(
    message.replaceAll(
      '{{ phoneNumber }}',
      `<a className="text-reset" href=tel:${phoneNumberLink}>${phoneNumber}</a>`
    )
  );

export const emailAddress = window.ecom?.craft3config?.emailAddress;

export const toSentence = (arrayOfStrings) =>
  arrayOfStrings.join(', ').replace(/,\s([^,]+)$/, ' and $1');

export const freezePage = () => {
  document.onkeydown = () => false;
  document.body.classList.add('no-click');
};

export const releasePage = () => {
  document.onkeydown = null;
  document.body.classList.remove('no-click');
};

// Get the current basename for React Route wrappers in 1SLK
export const getSeaLinkBasename = () => {
  const tenantName = window.location.pathname.split('/')[1];

  return tenantName || '/';
};

// Generate ranges
export const generateRanges = (min, max, step = 0.1) => {
  const result = [min];

  while (result.slice(-1)[0] < max) {
    const newNumber = result.slice(-1)[0] + step;

    result.push(Number(newNumber.toFixed(1)));
  }

  return result;
};

/**
 * Match /bookings/ferry, /bookings/:bookingId/ferry
 * returns A boolean value.
 */
export const isInFerryBookingPage = (pathname) =>
  !!pathname.match(
    /\/bookings\/\d+$|\/bookings\/\d+\/ferry|\/bookings\/ferry/i
  );

/**
 * Handles a redirection whether its in the navigation flow or not.
 */
export const historyOrReload = (history, path, reload) => {
  if (history && !reload) {
    history.push(path);
  } else {
    window.location = `${ecom.craft3config.api_prefix}${path}`;
  }
};

export const getQuicktravelUrl = () => window.ecom.craft3config.quicktravel_url;

/* Format accommodation property address */

export const formattedPropertyAddress = (property) =>
  `${property?.address?.address_line1}${
    property?.address?.address_line2 ? ' ' : ''
  }${property?.address?.address_line2},  ${property?.address?.post_code} ${
    property?.address?.city
  }, ${property?.address?.state}`;

// [null, null, null, true, true, true] -> [3, 4, 5]
// [null, true, null, true] -> [1, 3]
// https://stackoverflow.com/a/64670292
export const arrayKeyIndexes = (arr) => [...arr.keys()].filter((i) => arr[i]);

export const trackError = (error, message) => {
  if (window.Rollbar) {
    window.Rollbar.error(message || error.message, error);
  }
  console.error(error);
};

export const getFirstAvailableDeparture = (departures) => {
  const today = dateFormat(new Date(), FORMAT_QT_DATE);

  const availableDepartures = departures.filter(
    (departure) =>
      departure.available === true && departure.departs_on === today
  );

  return availableDepartures[0] ?? {};
};
